/**
 * Página /tenant/calendar - Calendário Global
 *
 * Mostra eventos unificados de todas as fontes (manual, agendamentos,
 * atividades CRM, tarefas, Ordens de Serviço, Google Calendar) em um FullCalendar com
 * visualização mês/semana/dia.
 *
 * Layout premium estilo Google Calendar:
 *  - Toolbar superior controlada (Hoje + prev/next + título + view switcher)
 *  - Sidebar à esquerda (calendários + filtros de fonte)
 *  - Card do calendário com altura fixa e scroll interno
 *  - Configurações em modal (não navega para outra página)
 *
 * RELEASE:
 *   2026-10-16 v2.3.0 - Criação customizada opt-in (BR-OS-031): props
 *                       `onCreateRequest`/`createLabel`. Quando definidas, o botão
 *                       principal e o clique/arraste no grid delegam a criação ao
 *                       chamador (ex.: modal de criar OS na Agenda de OS), o menu
 *                       de outras fontes é ocultado e o gate deixa de exigir
 *                       calendário editável. Sem o prop, comportamento inalterado.
 *   2026-10-10 v2.2.0 - Props para modo modal reutilizável (BR-OS-031): hideSidebar,
 *                       initialDate/initialView, forcedSources, eventFilter, eventColor,
 *                       focusEventId e toolbarExtra. Página padrão inalterada.
 *   2026-10-09 v1.9.0 - Inclui Ordem de Serviço nas fontes padrão do calendário.
 *   2026-07-25 v1.8.0 - Correção definitiva do popover nativo "+N mais":
 *                       remove a trava de primeiro clique/reabertura
 *                       programática e volta a abrir o popover diretamente,
 *                       pois o modo mês agora recebe o range completo via
 *                       cursor server-side em useCalendarMonthSummary v3.1.0.
 *   2026-07-25 v1.7.9 - Correção cirúrgica do popover nativo "+N mais":
 *                       impede a abertura antes do dia completo carregar,
 *                       busca os eventos com o hook já existente e reabre o
 *                       próprio link do FullCalendar com trava anti-loop. Evita
 *                       popover parcial com apenas top-K registros após a
 *                       divisão das RPCs.
 *   2026-07-25 v1.7.8 - Remove o carregamento sob demanda por hover/focus do
 *                       link "+N mais". O mês passa a receber todos os eventos
 *                       já na abertura via hook paginado server-side, evitando
 *                       que os 6 eventos visíveis do dia sejam trocados quando
 *                       o usuário passa o mouse no contador. Também fixa a
 *                       ordenação do FullCalendar por start/id.
 *   2026-07-25 v1.7.7 - Estabiliza o rótulo "+N mais" no modo Mês. O
 *                       `moreLinkDidMount` só disparava uma vez, então após o
 *                       merge do dia completo (carregado no hover) o
 *                       FullCalendar reescrevia o texto com base no seu
 *                       próprio `arg.num`, causando o efeito "+10 → +12" ao
 *                       passar o mouse. Agora um `useEffect` + `MutationObserver`
 *                       reaplica continuamente o total real vindo de
 *                       `monthSummary.perDay[data-date]` e reanexa os handlers
 *                       de pré-carregamento em cada re-render. Combina com a
 *                       RPC v1.11.0 (top-K por start_at/id) que impede a
 *                       reordenação dos primeiros N slots após o merge.
 *   2026-07-24 v1.7.6 - Popover "+N mais" passa a carregar sob demanda o
 *                       dia completo antes/durante a abertura. O mês segue
 *                       usando top-K por performance, mas ao clicar no link a
 *                       RPC existente `get_calendar_unified_events` busca todos
 *                       os eventos daquele dia, mescla no estado local e permite
 *                       que o popover nativo tenha itens suficientes para ativar
 *                       a rolagem interna sem trocar o layout visual.
 *   2026-07-24 v1.7.5 - Scroll interno do popover "+N mais" corrigido em
 *                       definitivo. Replica o padrão do AppLauncherPopover
 *                       (v1.4.1): MutationObserver global observa a injeção
 *                       de `.fc-popover` pelo FullCalendar e seta em runtime
 *                       a variável CSS `--fc-popover-available-height` com o
 *                       espaço vertical realmente disponível, garantindo que
 *                       o body ative overflow-y:auto ao invés de transbordar.
 *   2026-07-24 v1.6.1 - Elimina duplo/triplo fetch de get_calendar_unified_events.
 *                       Range das queries agora é derivado por modo:
 *                       `listRange` via useMemo(currentView, listAnchorDate) e
 *                       `calendarRange` alimentado exclusivamente por datesSet
 *                       do FullCalendar. Remove a janela de "range obsoleto"
 *                       entre o toggle de modo e o effect de sincronização.
 *   2026-07-24 v1.6.0 - Scroll infinito na visualização Lista (paridade Agenda)
 *                       via useCalendarUnifiedEventsInfinite + remoção do
 *                       wrapper de scroll duplicado.
 *   2026-06-25 v1.5.0 - Visualização em Lista (reuso SchedulingListView via adaptador)
 *   2026-06-25 v1.4.0 - Popover de seleção de fonte ao clicar em "Novo evento"
 *   2026-06-24 v1.3.0 - Toolbar movida para dentro do card do calendário + mini calendário no sidebar
 *   2026-06-24 v1.2.0 - Toolbar customizada, settings em modal, layout premium
 *   2026-06-24 v1.1.0 - Fase 3: CRUD manual + drag-drop + botão "Novo evento"
 *   2026-06-24 v1.0.0 - Versão inicial (Fase 2 Calendário Global)
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useCalendars } from '@/hooks/useCalendars';
import {
  useCalendarUnifiedEvents,
  type CalendarEventSourceType,
  type UnifiedCalendarEvent,
} from '@/hooks/useCalendarUnifiedEvents';
// v1.6.0 (scroll infinito modo Lista) — paridade com a tela Agenda
import { useCalendarUnifiedEventsInfinite } from '@/hooks/useCalendarUnifiedEventsInfinite';
// v1.7.0: resumo agregado do mês (fix do cap de 1000 eventos do PostgREST).
import { useCalendarMonthSummary } from '@/hooks/useCalendarMonthSummary';
import { useManualCalendarEvent } from '@/hooks/useManualCalendarEvent';
import { CalendarSidebar } from '@/components/calendar/CalendarSidebar';
import { ManualEventModal } from '@/components/calendar/ManualEventModal';
// v2.2.0: normaliza seleções "dia inteiro" (Mês) em eventos com hora/minuto
import { resolveDefaultEventTimes } from '@/components/calendar/calendarEventDefaults';

import { CalendarEventContent } from '@/components/calendar/CalendarEventContent';
import { CalendarSettingsModal } from '@/components/calendar/CalendarSettingsModal';
import { CalendarEventModalRouter } from '@/components/calendar/CalendarEventModalRouter';
// v2.1.0 (UX): popover de resumo ancorado no evento (estilo Google Agenda)
import { CalendarEventQuickPopover } from '@/components/calendar/CalendarEventQuickPopover';

import { NewEventPopover, type NewEventSource } from '@/components/calendar/NewEventPopover';
// v1.9.0: fonte única das fontes disponíveis (gate por módulo contratado)
import { useAvailableCalendarSources } from '@/hooks/useAvailableCalendarSources';
import { TaskFormDialog } from '@/components/tasks/modal/TaskFormDialog';
import { InternalBookingDialog } from '@/components/crm/activities/InternalBookingDialog';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Plus, ChevronLeft, ChevronRight, ChevronDown, List, CalendarDays } from 'lucide-react';
import { SchedulingListView } from '@/components/crm/activities/SchedulingListView';
import { unifiedToActivityItem } from '@/components/calendar/unifiedToActivityItem';
import { DateRangeJumpPicker } from '@/components/calendar/DateRangeJumpPicker';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
// Reusa CSS .fc-event-completed (risco + opacidade) do calendário CRM
import '@/components/crm/activities/calendar/calendar-styles.css';

const logger = createLogger('CalendarWorkspace');

const DEFAULT_SOURCES: CalendarEventSourceType[] = [
  'manual',
  'booking',
  'activity',
  'task',
  'service_order',
  'scheduled_message',
  'google',
];

type CalendarView = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay';

/**
 * Preferências do usuário persistidas em localStorage para que a tela
 * mantenha a mesma configuração entre sessões/navegações.
 */
const PREFS_KEY = 'tenant.calendar.preferences.v1';

interface CalendarPrefs {
  viewMode?: 'calendar' | 'list';
  currentView?: CalendarView;
  selectedCalendarIds?: string[] | null;
  selectedSources?: CalendarEventSourceType[];
  /** v1.7.0: mostrar concluídos/cancelados (default true) */
  includeFinished?: boolean;
}

/** Lê preferências do localStorage (tolerante a falhas/SSR/modo privado). */
function readPrefs(): CalendarPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    return raw ? (JSON.parse(raw) as CalendarPrefs) : {};
  } catch {
    return {};
  }
}

/** Mescla e grava preferências (parcial) no localStorage. */
function writePrefs(partial: CalendarPrefs) {
  try {
    const current = readPrefs();
    localStorage.setItem(PREFS_KEY, JSON.stringify({ ...current, ...partial }));
  } catch {
    // Ignora falhas silenciosamente (modo privado/SSR).
  }
}

/** Ordenação estável equivalente ao ORDER BY final da RPC (start_at ASC, id ASC). */
function sortEventsByStartThenId(events: UnifiedCalendarEvent[]) {
  return [...events].sort((a, b) => {
    const startDiff = new Date(a.start_at).getTime() - new Date(b.start_at).getTime();
    if (startDiff !== 0) return startDiff;
    return String(a.id).localeCompare(String(b.id));
  });
}

interface CalendarWorkspaceProps {
  /**
   * Quando `true`, o workspace é renderizado dentro de um contêiner controlado
   * (ex.: modal). Nesse modo a altura passa a ser 100% do pai, em vez da altura
   * da viewport usada na página /tenant/calendar.
   */
  embedded?: boolean;
  /** v2.2.0: oculta o menu lateral (modo modal reutilizável) */
  hideSidebar?: boolean;
  /** v2.2.0: data inicial exibida (ex.: data do agendamento) */
  initialDate?: string | Date;
  /** v2.2.0: visão inicial forçada (ignora preferência salva) */
  initialView?: CalendarView;
  /** v2.2.0: fontes fixas; quando informado, não lê/grava preferências */
  forcedSources?: CalendarEventSourceType[];
  /** v2.2.0: filtro extra aplicado aos eventos já carregados */
  eventFilter?: (e: UnifiedCalendarEvent) => boolean;
  /** v2.2.0: cor customizada por evento (ex.: cor do tipo de serviço) */
  eventColor?: (e: UnifiedCalendarEvent) => string | null | undefined;
  /** v2.2.0: id do evento (ou source_id da origem) a destacar */
  focusEventId?: string | null;
  /** v2.2.0: barra extra de filtros acima do calendário */
  toolbarExtra?: ReactNode;
  /**
   * v2.3.0: criação customizada (opt-in). Quando definido, o botão principal
   * e o clique/arraste no grid chamam este callback em vez de abrir o modal
   * de evento manual, e o menu de outras fontes é ocultado. `slot` é null
   * quando a criação parte do botão (sem horário escolhido).
   */
  onCreateRequest?: (slot: { start: Date; end: Date; allDay: boolean } | null) => void;
  /** v2.3.0: rótulo do botão principal de criação (padrão "Novo evento") */
  createLabel?: string;
}

export function CalendarWorkspace({
  embedded = false,
  hideSidebar = false,
  initialDate,
  initialView,
  forcedSources,
  eventFilter,
  eventColor,
  focusEventId,
  toolbarExtra,
  onCreateRequest,
  createLabel = 'Novo evento',
}: CalendarWorkspaceProps) {
  // v2.2.0: modo modal (fontes fixas) não usa preferências persistidas
  const persistPrefs = !forcedSources;
  // Ref do FullCalendar para controlar via API
  const calendarRef = useRef<FullCalendar | null>(null);

  // v1.6.1: `range` deixou de ser um `useState` monolítico compartilhado por
  // ambos os modos (fonte do duplo fetch). Agora:
  //  - Modo Calendário: `calendarRange` é state, atualizado APENAS pelo
  //    handler `datesSet` do FullCalendar (fonte de verdade nesse modo).
  //  - Modo Lista: `listRange` é um `useMemo` derivado sincronicamente de
  //    (currentView, listAnchorDate) — sem effect intermediário.
  //  - `activeRange` seleciona qual alimenta as queries no render corrente,
  //    eliminando o descompasso que causava 2–3 requisições por toggle.
  const [calendarRange, setCalendarRange] = useState<{
    start: string;
    end: string;
  } | null>(null);

  // Preferências persistidas (lidas uma única vez no mount)
  const initialPrefs = useMemo<CalendarPrefs>(() => (persistPrefs ? readPrefs() : {}), []);

  // Estado da toolbar customizada
  const [viewTitle, setViewTitle] = useState<string>('');
  const [currentView, setCurrentView] = useState<CalendarView>(
    initialView ?? initialPrefs.currentView ?? 'dayGridMonth'
  );
  /** Modo de visualização: calendário (FullCalendar) ou lista (SchedulingListView reaproveitado) */
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>(
    forcedSources ? 'calendar' : initialPrefs.viewMode ?? 'calendar'
  );

  // Filtros
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<string[] | null>(
    initialPrefs.selectedCalendarIds ?? null
  );
  const [selectedSources, setSelectedSources] = useState<CalendarEventSourceType[]>(
    forcedSources ?? initialPrefs.selectedSources ?? DEFAULT_SOURCES
  );
  // v1.7.0: toggle "Mostrar concluídos/cancelados"
  const [includeFinished, setIncludeFinished] = useState<boolean>(
    initialPrefs.includeFinished ?? true
  );

  // Modais
  const [modalOpen, setModalOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [taskFormOpen, setTaskFormOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<UnifiedCalendarEvent | null>(null);
  // Evento selecionado para o roteador de modais (detalhes por fonte)
  const [selectedEvent, setSelectedEvent] = useState<UnifiedCalendarEvent | null>(null);
  /**
   * v2.1.0 (UX): resumo rápido do evento clicado no grid.
   * Guarda o evento e o retângulo do bloco clicado (âncora do popover).
   */
  const [quickView, setQuickView] = useState<{
    event: UnifiedCalendarEvent;
    rect: DOMRect;
    el: HTMLElement;
  } | null>(null);

  /**
   * v2.0.0 (UX): pré-preenchimento do modal de evento manual.
   * Alimentado pelo clique/seleção no grid do calendário ou pelo Quick Create
   * (?new=google), evitando que o usuário redigite data/horário.
   */
  const [manualPrefill, setManualPrefill] = useState<{
    startAt?: string;
    endAt?: string;
    allDay?: boolean;
    googleSync?: boolean;
  } | null>(null);

  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  /**
   * Handler: resolve a fonte escolhida para o modal de criação correspondente.
   * v2.0.0: 'manual' e 'google' usam o mesmo modal; 'google' apenas já entra
   * com o toggle de sincronização ligado.
   */
  const handlePickSource = (source: NewEventSource) => {
    setEditingEvent(null);
    switch (source) {
      case 'manual':
      case 'google':
        setManualPrefill({ googleSync: source === 'google' });
        setModalOpen(true);
        break;
      case 'task':
        setTaskFormOpen(true);
        break;
      case 'booking':
        setBookingOpen(true);
        break;
      case 'scheduled_message':
        toast.info('Selecione uma conversa para agendar uma mensagem.');
        navigate('/chat');
        break;
    }
  };


  // Quick Create (?new=manual|google|booking): dispara o mesmo handler
  // do popover "Novo evento", reaproveitando a resolução por fonte.
  useAutoOpenFromQuery('manual', () => handlePickSource('manual'));
  useAutoOpenFromQuery('google', () => handlePickSource('google'));
  useAutoOpenFromQuery('booking', () => handlePickSource('booking'));


  // Mini calendário (sidebar)
  const [miniSelectedDate, setMiniSelectedDate] = useState<Date | undefined>(undefined);

  // Âncora do mês para o modo Lista (FullCalendar não está montado nesse modo,
  // então gerimos a janela visível manualmente). Inicializa em "hoje".
  const [listAnchorDate, setListAnchorDate] = useState<Date>(() => (initialDate ? new Date(initialDate) : new Date()));

  /**
   * Calcula janela [start, end] e título para um determinado mês no modo Lista.
   * Replica o comportamento do FullCalendar (mês inteiro) para que o hook
   * `useCalendarUnifiedEvents` traga eventos do mês selecionado.
   */
  const computeMonthRange = (date: Date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 1);
    const title = date.toLocaleDateString('pt-BR', {
      month: 'long',
      year: 'numeric',
    });
    return { start: start.toISOString(), end: end.toISOString(), title };
  };

  /**
   * Janela semanal (domingo → sábado) — casa com firstDay=0 do FullCalendar.
   */
  const computeWeekRange = (date: Date) => {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const start = new Date(d);
    start.setDate(d.getDate() - d.getDay()); // volta para domingo
    const end = new Date(start);
    end.setDate(start.getDate() + 7);
    const endLabel = new Date(end);
    endLabel.setDate(end.getDate() - 1);
    const fmt = (x: Date) =>
      x.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' });
    const title = `${fmt(start)} – ${fmt(endLabel)} de ${start.getFullYear()}`;
    return { start: start.toISOString(), end: end.toISOString(), title };
  };

  /**
   * Janela diária — 1 dia calendário.
   */
  const computeDayRange = (date: Date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const end = new Date(start);
    end.setDate(start.getDate() + 1);
    const title = start.toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    return { start: start.toISOString(), end: end.toISOString(), title };
  };

  // v1.6.1: Range e título do modo Lista derivados sincronicamente. Substitui
  // o `useEffect` que fazia `setRange(...)` num render posterior — origem do
  // duplo fetch (fetch com range antigo + refetch com range novo).
  const listComputed = useMemo(() => {
    const computer =
      currentView === 'timeGridWeek'
        ? computeWeekRange
        : currentView === 'timeGridDay'
          ? computeDayRange
          : computeMonthRange;
    return computer(listAnchorDate);
  }, [currentView, listAnchorDate]);

  const listRange = useMemo(
    () => ({ start: listComputed.start, end: listComputed.end }),
    [listComputed],
  );

  /**
   * Range efetivo consumido pelas queries no render corrente.
   * - Modo Lista: `listRange` (memo derivado).
   * - Modo Calendário: `calendarRange` (state alimentado pelo `datesSet`).
   * Fica `null` no primeiríssimo render do modo Calendário até o FullCalendar
   * reportar o grid — o `enabled` das queries impede fetch nesse intervalo.
   */
  const activeRange = viewMode === 'list' ? listRange : calendarRange;

  /**
   * Título exibido na toolbar. No modo Lista deriva do `listComputed`; no modo
   * Calendário vem do state `viewTitle` (setado pelo `datesSet` do FullCalendar).
   */
  const displayTitle = viewMode === 'list' ? listComputed.title : viewTitle;

  // Full-sync
  const [syncingCalendarId, setSyncingCalendarId] = useState<string | null>(null);

  const { calendars, isLoading: loadingCalendars } = useCalendars();
  const { updateEventAsync, deleteEventAsync } = useManualCalendarEvent();

  // Inicializa / sincroniza seleção:
  //  - Se nada salvo: todos checados ao carregar a lista.
  //  - Se havia IDs salvos: descarta os que não existem mais em `calendars`.
  // v1.6.1: convertido de `useMemo` para `useEffect`. É side-effect (não
  // computação) — rodar em fase de render disparava setState durante render
  // e mudava identidade do array `selectedCalendarIds` gerando fetch extra.
  useEffect(() => {
    if (calendars.length === 0) return;
    if (selectedCalendarIds === null) {
      setSelectedCalendarIds(calendars.map((c) => c.id));
      return;
    }
    const validIds = new Set(calendars.map((c) => c.id));
    const filtered = selectedCalendarIds.filter((id) => validIds.has(id));
    if (filtered.length !== selectedCalendarIds.length) {
      setSelectedCalendarIds(filtered);
    }
  }, [calendars, selectedCalendarIds]);

  // v1.9.0: fontes disponíveis dependem dos módulos contratados pelo tenant
  // e da existência de integração Google. Qualquer fonte indisponível é
  // removida da seleção persistida (evita filtrar por algo que não existe).
  const hasGoogleIntegration = useMemo(
    () => calendars.some((c) => !!c.google_integration_id),
    [calendars],
  );
  const availableSources = useAvailableCalendarSources({ hasGoogleIntegration });
  useEffect(() => {
    const allowed = new Set(availableSources);
    const filtered = selectedSources.filter((s) => allowed.has(s));
    if (filtered.length !== selectedSources.length) {
      setSelectedSources(filtered);
    }
  }, [availableSources, selectedSources]);

  // v1.6.1: referência estável de `selectedCalendarIds` para a queryKey.
  // Ordena para evitar refetch em caso de reordenação futura do array.
  const stableCalendarIds = useMemo(
    () =>
      selectedCalendarIds ? [...selectedCalendarIds].sort() : null,
    [selectedCalendarIds],
  );

  // Persistência das preferências em localStorage
  useEffect(() => {
    if (persistPrefs) writePrefs({ viewMode });
  }, [viewMode]);
  useEffect(() => {
    if (persistPrefs) writePrefs({ currentView });
  }, [currentView]);
  useEffect(() => {
    if (persistPrefs) writePrefs({ selectedSources });
  }, [selectedSources]);
  useEffect(() => {
    // Só grava após a primeira hidratação (evita gravar `null` sobre uma seleção válida)
    if (selectedCalendarIds !== null) {
      if (persistPrefs) writePrefs({ selectedCalendarIds });
    }
  }, [selectedCalendarIds]);
  useEffect(() => {
    if (persistPrefs) writePrefs({ includeFinished });
  }, [includeFinished]);

  /**
   * v1.7.5 — Correção definitiva do scroll interno do popover "+N mais".
   *
   * O FullCalendar injeta `.fc-popover` no DOM (fora do subtree do React) com
   * `top/left` inline calculados a partir da posição da célula do dia. Um
   * MutationObserver no <body> detecta essa injeção e, para cada popover,
   * define a variável CSS `--fc-popover-available-height` = espaço real
   * restante até a borda inferior da viewport (com 16px de margem). O CSS em
   * `calendar-styles.css` consome essa variável em `max-height !important`,
   * ativando `overflow-y:auto` no body sempre que a lista extrapolar.
   *
   * Padrão espelhado do `AppLauncherPopover` (v1.4.1), que usa
   * `--radix-popover-content-available-height`. Aqui replicamos manualmente
   * porque FullCalendar não é Radix.
   */
  useEffect(() => {
    const AVAIL_VAR = '--fc-popover-available-height';
    const BOTTOM_MARGIN = 16;

    // Ajusta a variável CSS de um popover específico com base na sua posição.
    const applyAvailableHeight = (el: HTMLElement) => {
      const update = () => {
        const top = el.getBoundingClientRect().top;
        const available = Math.max(window.innerHeight - top - BOTTOM_MARGIN, 160);
        el.style.setProperty(AVAIL_VAR, `${available}px`);
      };

      // Executa imediatamente e novamente no próximo frame: o FullCalendar
      // aplica posição inline durante a montagem, então o segundo cálculo pega
      // a posição final sem alterar o layout visual do popover.
      update();
      window.requestAnimationFrame(update);
    };

    // Handler de resize compartilhado — recalcula para popovers ainda montados.
    const handleResize = () => {
      document
        .querySelectorAll<HTMLElement>('.fc-popover')
        .forEach(applyAvailableHeight);
    };

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          // O popover pode ser o próprio nó ou estar dentro dele.
          const popovers: HTMLElement[] = [];
          if (node.classList?.contains('fc-popover')) popovers.push(node);
          node.querySelectorAll?.<HTMLElement>('.fc-popover').forEach((p) =>
            popovers.push(p),
          );
          popovers.forEach(applyAvailableHeight);
        });
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);


  // Modo Calendário (Mês/Semana/Dia): hook não paginado — o FullCalendar precisa
  // da janela inteira renderizada de uma vez. Desabilitado no modo Lista.
  // v1.6.1: só habilita após o primeiro `datesSet` popular `calendarRange`.
  // v1.7.0: no MÊS, usamos `useCalendarMonthSummary` (JSONB, sem cap de 1000 do
  //         PostgREST) — os top K eventos por dia bastam para o grid, e o
  //         `total_count` do resumo alimenta o contador do header. Semana/Dia
  //         continuam usando o hook original (janelas curtas, sem risco de cap).
  const isMonthCalendar = viewMode === 'calendar' && currentView === 'dayGridMonth';

  const { data: calendarEvents = [], isLoading: loadingCalendarEvents } =
    useCalendarUnifiedEvents({
      start: calendarRange?.start ?? '',
      end: calendarRange?.end ?? '',
      calendarIds: stableCalendarIds,
      sourceTypes: selectedSources,
      includeFinished,
      enabled:
        viewMode === 'calendar' &&
        !isMonthCalendar &&
        !!calendarRange &&
        !!stableCalendarIds &&
        stableCalendarIds.length > 0,
    });

  // v1.7.2: constantes de visibilidade por dia (mês). O hook mensal v3.1.0
  // carrega o mês completo via cursor; o FullCalendar decide quais eventos
  // ficam visíveis e monta o popover nativo com a lista completa do dia.
  const DAY_MAX_EVENTS_DESKTOP = 6;
  const DAY_MAX_EVENTS_MOBILE = 2;
  const dayMaxEventsCurrent = isMobile
    ? DAY_MAX_EVENTS_MOBILE
    : DAY_MAX_EVENTS_DESKTOP;

  const { data: monthSummary, isLoading: loadingMonthSummary } =
    useCalendarMonthSummary({
      start: calendarRange?.start ?? '',
      end: calendarRange?.end ?? '',
      calendarIds: stableCalendarIds,
      sourceTypes: selectedSources,
      includeFinished,
      enabled:
        isMonthCalendar &&
        !!calendarRange &&
        !!stableCalendarIds &&
        stableCalendarIds.length > 0,
    });

  // Modo Lista: scroll infinito via cursor (paridade com useSchedulingListActivities).
  // Reusa a mesma RPC (v1.8.0+), opt-in via p_limit + cursor.
  // v1.6.1: consome `listRange` (memo síncrono) — sem lag entre toggle e effect.
  const {
    events: listEvents,
    isLoading: loadingListEvents,
    hasNextPage: listHasNextPage,
    fetchNextPage: listFetchNextPage,
    isFetchingNextPage: listIsFetchingNextPage,
  } = useCalendarUnifiedEventsInfinite({
    start: listRange.start,
    end: listRange.end,
    calendarIds: stableCalendarIds,
    sourceTypes: selectedSources,
    includeFinished,
    enabled:
      viewMode === 'list' &&
      !!stableCalendarIds &&
      stableCalendarIds.length > 0,
  });

  // Fonte de dados vigente para handlers de clique/drag.
  //  - Lista:            listEvents (scroll infinito)
  //  - Mês (Calendário): monthSummary.events (mês completo via cursor server-side)
  //  - Semana/Dia:       calendarEvents (hook não paginado, janela curta)
  const events = useMemo<UnifiedCalendarEvent[]>(() => {
    if (viewMode === 'list') return listEvents;
    if (isMonthCalendar) {
      return sortEventsByStartThenId(monthSummary?.events ?? []);
    }
    return calendarEvents;
  }, [viewMode, isMonthCalendar, listEvents, monthSummary, calendarEvents]);

  // ─────────────────────────────────────────────────────────────────────────
  // v1.7.7 — Sincronização autoritativa do rótulo "+N mais" no modo Mês.
  //
  // Problema original: `moreLinkDidMount` do FullCalendar só dispara UMA vez
  // por mount do link e o texto nativo é derivado do conjunto interno do FC.
  //
  // Solução: manter o total real em uma ref e reaplicar o texto sempre que
  // (a) o resumo/fcEvents mudarem, ou (b) o FullCalendar recriar/atualizar
  // os links (observado via MutationObserver no root do calendário).
  // v1.7.8: não há mais preload no hover; os eventos do mês já chegam completos
  // via cursor server-side para manter os 6 slots visíveis estáveis.
  // ─────────────────────────────────────────────────────────────────────────
  const perDayTotalsRef = useRef<Record<string, number>>({});

  // Mantém a ref sincronizada com o último snapshot do resumo mensal.
  useEffect(() => {
    perDayTotalsRef.current = monthSummary?.perDay ?? {};
  }, [monthSummary]);

  useEffect(() => {
    if (!isMonthCalendar) return;

    // Reescreve o texto de todos os links "+N mais" visíveis usando o total
    // real do dia (perDay do resumo). Idempotente — pode rodar N vezes.
    const syncMoreLinks = () => {
      const links = document.querySelectorAll<HTMLElement>('.fc-daygrid-more-link');
      links.forEach((link) => {
        const dayCell = link.closest<HTMLElement>('.fc-daygrid-day[data-date]');
        const dateKey = dayCell?.dataset.date;
        if (!dateKey) return;

        const total = perDayTotalsRef.current[dateKey];
        if (typeof total === 'number') {
          const hidden = Math.max(total - dayMaxEventsCurrent, 0);
          const label = `+${hidden} mais`;
          if (link.textContent !== label) {
            link.textContent = label;
            link.setAttribute('aria-label', `${hidden} eventos adicionais`);
          }
        }
      });
    };

    // 1) Passagem inicial + após cada mudança de deps (monthSummary, fcEvents).
    //    `requestAnimationFrame` garante que o FullCalendar já pintou a
    //    célula/link antes de tentarmos localizar o DOM.
    const raf = window.requestAnimationFrame(syncMoreLinks);

    // 2) Reage a reescritas internas do FullCalendar. Escopo restrito ao
    //    container do FC para não vazar overhead.
    const fcRoot = document.querySelector<HTMLElement>('.fc');
    if (!fcRoot) {
      return () => window.cancelAnimationFrame(raf);
    }

    const observer = new MutationObserver(() => {
      // Debounce simples via rAF — evita loop quando nós mesmos alteramos texto.
      window.requestAnimationFrame(syncMoreLinks);
    });
    observer.observe(fcRoot, { childList: true, subtree: true, characterData: true });

    return () => {
      window.cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [isMonthCalendar, monthSummary, events, dayMaxEventsCurrent]);

  const loadingEvents =
    viewMode === 'list'
      ? loadingListEvents
      : isMonthCalendar
        ? loadingMonthSummary
        : loadingCalendarEvents;

  /**
   * Total EXATO de eventos exibidos no cabeçalho. No modo Mês vem do
   * `total_count` do resumo (evita o cap de 1000 do PostgREST); nos demais
   * modos usamos o comprimento da lista carregada.
   */
  const displayedEventCount = isMonthCalendar
    ? (monthSummary?.totalCount ?? 0)
    : events.length;

  const hasEditableCalendar = useMemo(
    () =>
      calendars.some(
        (c) => c.user_permission === 'edit' || c.user_permission === 'manage'
      ),
    [calendars]
  );

  // v2.3.0: com criação customizada (onCreateRequest), a permissão é do módulo
  // chamador (ex.: criar OS), não da edição de calendários manuais.
  const canCreate = onCreateRequest ? true : hasEditableCalendar;

  const handleToggleCalendar = (id: string) => {
    setSelectedCalendarIds((prev) => {
      const list = prev ?? calendars.map((c) => c.id);
      return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
    });
  };

  const handleToggleSource = (src: CalendarEventSourceType) => {
    setSelectedSources((prev) =>
      prev.includes(src) ? prev.filter((x) => x !== src) : [...prev, src]
    );
  };

  const handleFullSync = async (calendarId: string) => {
    try {
      setSyncingCalendarId(calendarId);
      const { data, error } = await supabase.functions.invoke('calendar-full-sync', {
        body: { calendar_id: calendarId },
      });
      if (error) throw error;
      logger.info('Full sync concluído', data);
      toast.success(
        `Sincronizado: ${data?.upserted ?? 0} eventos atualizados, ${data?.deleted ?? 0} removidos`
      );
    } catch (err: any) {
      logger.error('Erro no full sync', err);
      toast.error(err?.message ?? 'Erro ao sincronizar');
    } finally {
      setSyncingCalendarId(null);
    }
  };

  /**
   * Clique vindo da LISTA (sem elemento âncora): abre direto o modal completo.
   */
  const handleEventClick = (eventId: string) => {
    const ev = events.find((e) => e.id === eventId);
    if (!ev) return;
    // Delega para o roteador, que escolhe o modal correto por fonte.
    setSelectedEvent(ev);
  };

  /**
   * v2.1.0 — Clique no GRID do calendário: abre o popover de resumo ancorado
   * no bloco do evento, em vez de abrir o modal em primeiro plano.
   */
  const handleGridEventClick = (eventId: string, el: HTMLElement) => {
    const ev = events.find((e) => e.id === eventId);
    if (!ev) return;
    setQuickView({ event: ev, rect: el.getBoundingClientRect(), el });
  };

  /**
   * Mantém a âncora do popover alinhada ao evento durante scroll/resize.
   * Fecha o popover quando o bloco sai da viewport ou é removido do DOM.
   */
  useEffect(() => {
    if (!quickView) return;

    const reposition = () => {
      setQuickView((prev) => {
        if (!prev) return prev;
        if (!document.body.contains(prev.el)) return null;
        const rect = prev.el.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return null;
        return { ...prev, rect };
      });
    };

    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [quickView?.el]);


  const handleEventChange = async (info: {
    event: { id: string; start: Date | null; end: Date | null; allDay: boolean };
    revert: () => void;
  }) => {
    const ev = events.find((e) => e.id === info.event.id);
    if (!ev || ev.source_type !== 'manual') {
      info.revert();
      return;
    }
    if (ev.user_permission !== 'edit' && ev.user_permission !== 'manage') {
      info.revert();
      toast.error('Sem permissão para mover este evento');
      return;
    }
    if (!info.event.start) {
      info.revert();
      return;
    }
    try {
      await updateEventAsync({
        eventId: ev.id,
        startAt: info.event.start.toISOString(),
        endAt: (info.event.end ?? info.event.start).toISOString(),
        allDay: info.event.allDay,
      });
    } catch {
      info.revert();
    }
  };

  // Eventos para o FullCalendar
  const fcEvents = useMemo(
    () =>
      (eventFilter ? events.filter(eventFilter) : events).map((e) => {
        const color = eventColor?.(e) || e.calendar_color;
        const editable =
          e.source_type === 'manual' &&
          (e.user_permission === 'edit' || e.user_permission === 'manage');
        // v1.6.0: risco visual em concluídos/cancelados (reusa CSS .fc-event-completed
        // do calendário CRM — importado abaixo).
        const isFinished = !!e.completed_at || !!e.cancelled_at;
        return {
          id: e.id,
          title: e.title,
          start: e.start_at,
          end: e.end_at,
          allDay: e.all_day,
          backgroundColor: color,
          borderColor: color,
          editable,
          classNames: [
            ...(isFinished ? ['fc-event-completed'] : []),
            ...(focusEventId && (e.id === focusEventId || e.source_id === focusEventId) ? ['ring-2', 'ring-primary', 'ring-offset-1'] : []),
          ],
          extendedProps: {
            sourceType: e.source_type,
            permission: e.user_permission,
            reminders: e.reminders ?? [],
            recurrenceRule: e.recurrence_rule ?? null,
            completedAt: e.completed_at ?? null,
            cancelledAt: e.cancelled_at ?? null,
          },
        };
      }),
    [events, eventFilter, eventColor, focusEventId]
  );

  // Controles da toolbar customizada — no modo Lista o FullCalendar não está
  // montado (calendarRef = null), então navegamos pelo `listAnchorDate`.
  // Passo de navegação no modo Lista conforme o view selecionado
  const stepListAnchor = (dir: 1 | -1) => {
    setListAnchorDate((d) => {
      const nd = new Date(d);
      if (currentView === 'timeGridDay') nd.setDate(d.getDate() + dir);
      else if (currentView === 'timeGridWeek') nd.setDate(d.getDate() + 7 * dir);
      else nd.setMonth(d.getMonth() + dir); // mês
      return nd;
    });
  };

  const goPrev = () => {
    if (viewMode === 'list') return stepListAnchor(-1);
    calendarRef.current?.getApi().prev();
  };
  const goNext = () => {
    if (viewMode === 'list') return stepListAnchor(1);
    calendarRef.current?.getApi().next();
  };
  const goToday = () => {
    if (viewMode === 'list') {
      setListAnchorDate(new Date());
      return;
    }
    calendarRef.current?.getApi().today();
  };

  /**
   * Salta para uma data arbitrária escolhida no DateRangeJumpPicker.
   * Reaproveita os mesmos caminhos de navegação já usados por prev/next
   * (não altera fetch, cache ou range — apenas move o âncora).
   */
  const handleJumpToDate = (date: Date) => {
    if (viewMode === 'list') {
      // No modo Lista o âncora define a janela recalculada por listComputed.
      setListAnchorDate(date);
      return;
    }
    // No modo Calendário o próprio FullCalendar dispara `datesSet`, que
    // sincroniza calendarRange/viewTitle/currentView e refaz o fetch.
    calendarRef.current?.getApi().gotoDate(date);
  };

  const changeView = (view: CalendarView) => {
    // No modo Lista o FullCalendar não está montado — apenas atualiza o state
    // e o useEffect de sincronização recalcula a janela.
    if (viewMode !== 'list') {
      calendarRef.current?.getApi().changeView(view);
    }
    setCurrentView(view);
  };

  // Datas com eventos para o mini calendário (agregadas localmente)
  const miniEventDates = useMemo(() => {
    const set = new Set<string>();
    const result: Date[] = [];
    for (const ev of events) {
      if (!ev.start_at) continue;
      const d = new Date(ev.start_at);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!set.has(key)) {
        set.add(key);
        result.push(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
      }
    }
    return result;
  }, [events]);

  // Handlers do mini calendário
  const handleMiniSelectDate = (date: Date | undefined) => {
    setMiniSelectedDate(date);
    if (!date) return;
    if (viewMode === 'list') {
      // No modo Lista navegamos pelo âncora mensal (sem FullCalendar).
      setListAnchorDate(new Date(date.getFullYear(), date.getMonth(), 1));
      return;
    }
    const api = calendarRef.current?.getApi();
    if (!api) return;
    // Filtra o calendário grande para o dia clicado (view Dia).
    // O mini calendário continua mostrando os indicadores de todos os dias do mês.
    api.gotoDate(date);
    api.changeView('timeGridDay');
    setCurrentView('timeGridDay');
  };

  const handleMiniMonthChange = (date: Date) => {
    if (viewMode === 'list') {
      setListAnchorDate(new Date(date.getFullYear(), date.getMonth(), 1));
      return;
    }
    calendarRef.current?.getApi().gotoDate(date);
  };


  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden gap-2',
        embedded ? 'h-full' : 'h-[calc(100vh-3.5rem)]',
      )}
    >
        {/* Linha: sidebar + calendário (toolbar agora vive DENTRO do card) */}
        <div className="flex flex-col lg:flex-row gap-2 flex-1 min-h-0">
          {!hideSidebar && <CalendarSidebar
            calendars={calendars}
            selectedCalendarIds={selectedCalendarIds ?? []}
            onToggleCalendar={handleToggleCalendar}
            selectedSources={selectedSources}
            onToggleSource={handleToggleSource}
            isLoading={loadingCalendars}
            onFullSync={handleFullSync}
            syncingCalendarId={syncingCalendarId}
            onOpenSettings={() => setSettingsOpen(true)}
            miniEventDates={miniEventDates}
            miniSelectedDate={miniSelectedDate}
            onMiniSelectDate={handleMiniSelectDate}
            onMiniMonthChange={handleMiniMonthChange}
            includeFinished={includeFinished}
            onToggleIncludeFinished={setIncludeFinished}
          />}

          <div className="flex-1 min-w-0 rounded-lg border border-border/40 bg-card flex flex-col min-h-0 overflow-hidden">
            {/* v2.2.0: filtros extras (ex.: Prestador/Tipo no modal de OS) */}
            {toolbarExtra && (
              <div className="flex flex-wrap items-center gap-2 shrink-0 border-b border-border/40 px-3 py-2">{toolbarExtra}</div>
            )}
            {/* Toolbar interna — fica contida no quadro do calendário */}
            <div className="flex items-center justify-between gap-3 shrink-0 flex-wrap px-3 py-2 border-b border-border/40 bg-muted/20">
              <div className="flex items-center gap-2 min-w-0">
                <Button variant="outline" size="sm" onClick={goToday} className="h-9">
                  Hoje
                </Button>
                <div className="flex items-center">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={goPrev}
                    className="h-9 w-9"
                    aria-label="Anterior"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={goNext}
                    className="h-9 w-9"
                    aria-label="Próximo"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </div>
                {/* v2.0.0: título atua como trigger do DateRangeJumpPicker,
                    economizando o botão dedicado. Ancoramos pelo midpoint do
                    range para corrigir o bug em que o mês exibido divergia do
                    título (ex.: "Mai 2026" no picker vs "Junho 2026" no h1),
                    já que `calendarRange.start` no modo Mês cai no domingo da
                    primeira semana visível — normalmente no mês anterior. */}
                <DateRangeJumpPicker
                  currentDate={
                    viewMode === 'list'
                      ? listAnchorDate
                      : calendarRange?.start && calendarRange?.end
                        ? new Date(
                            (new Date(calendarRange.start).getTime() +
                              new Date(calendarRange.end).getTime()) /
                              2,
                          )
                        : new Date()
                  }
                  view={currentView}
                  onPick={handleJumpToDate}
                  renderTrigger={({ label, open }) => (
                    <button
                      type="button"
                      aria-label="Selecionar período"
                      aria-haspopup="dialog"
                      aria-expanded={open}
                      title={label}
                      className={cn(
                        // Efeito sutil e moderno preservando visual de título
                        'group inline-flex items-center gap-1.5 rounded-md px-2 py-1 -mx-1',
                        'cursor-pointer select-none transition-colors',
                        'hover:bg-muted/60 ring-1 ring-transparent hover:ring-border/60',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60',
                        open && 'bg-muted/60 ring-border/60',
                      )}
                    >
                      <h1 className="text-base lg:text-lg font-semibold text-foreground truncate capitalize">
                        {displayTitle}
                      </h1>
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 opacity-60 transition-transform',
                          open && 'rotate-180',
                        )}
                      />
                    </button>
                  )}
                />
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {loadingEvents ? (
                  <span className="text-xs text-muted-foreground">Carregando…</span>
                ) : (
                  <span className="text-xs text-muted-foreground hidden sm:inline">
                    {displayedEventCount.toLocaleString('pt-BR')} evento
                    {displayedEventCount === 1 ? '' : 's'}
                  </span>
                )}
                {/* Toggle Lista / Calendário (reuso visual do CRM) */}
                <ToggleGroup
                  type="single"
                  size="sm"
                  value={viewMode}
                  onValueChange={(v) => {
                    if (!v) return;
                    const next = v as 'calendar' | 'list';
                    // Ao entrar no modo Lista, sincroniza o âncora com o mês
                    // que o usuário estava visualizando no FullCalendar.
                    if (next === 'list' && viewMode !== 'list') {
                      // v1.6.1: usa `calendarRange` (fonte de verdade do FullCalendar).
                      const base = calendarRange?.start
                        ? new Date(calendarRange.start)
                        : new Date();
                      // range.start do FullCalendar costuma cair em dias do mês anterior
                      // (grid de 6 semanas); avança ~7 dias para cair no mês "real".
                      const probe = new Date(base.getTime() + 7 * 24 * 60 * 60 * 1000);
                      setListAnchorDate(new Date(probe.getFullYear(), probe.getMonth(), 1));
                    }
                    setViewMode(next);
                  }}
                  className="rounded-md border border-border/60 bg-muted/30 p-0.5"
                >
                  <ToggleGroupItem value="list" className="h-8 px-3 text-xs gap-1.5">
                    <List className="h-3.5 w-3.5" /> Lista
                  </ToggleGroupItem>
                  <ToggleGroupItem value="calendar" className="h-8 px-3 text-xs gap-1.5">
                    <CalendarDays className="h-3.5 w-3.5" /> Calendário
                  </ToggleGroupItem>
                </ToggleGroup>

                {/* Switcher Mês/Semana/Dia — visível em ambos os modos (Lista e Calendário) */}
                <ToggleGroup
                  type="single"
                  size="sm"
                  value={currentView}
                  onValueChange={(v) => v && changeView(v as CalendarView)}
                  className="rounded-md border border-border/60 bg-muted/30 p-0.5"
                >
                  <ToggleGroupItem value="dayGridMonth" className="h-8 px-3 text-xs">
                    Mês
                  </ToggleGroupItem>
                  <ToggleGroupItem value="timeGridWeek" className="h-8 px-3 text-xs">
                    Semana
                  </ToggleGroupItem>
                  <ToggleGroupItem value="timeGridDay" className="h-8 px-3 text-xs">
                    Dia
                  </ToggleGroupItem>
                </ToggleGroup>
                {/* v2.0.0: botão único "Novo evento" (cria evento manual) +
                    menu secundário para as demais fontes (tarefa/agendamento). */}
                <div className="flex items-center">
                  <Button
                    size="xs"
                    disabled={!canCreate}
                    onClick={() => (onCreateRequest ? onCreateRequest(null) : handlePickSource('manual'))}
                    className={cn(
                      'hover:shadow-md transition-all duration-200',
                      // v2.3.0: sem menu de fontes, o botão fica com cantos completos
                      onCreateRequest ? '' : 'rounded-r-none',
                    )}
                  >
                    <Plus className="sm:mr-1.5" />
                    <span className="hidden sm:inline">{createLabel}</span>
                  </Button>
                  {/* v2.3.0: criação customizada tem um único destino — sem menu */}
                  {!onCreateRequest && (
                    <NewEventPopover
                      disabled={!canCreate}
                      onPick={handlePickSource}
                    >
                      <Button
                        size="xs"
                        disabled={!canCreate}
                        aria-label="Outras opções de criação"
                        className="rounded-l-none border-l border-primary-foreground/20 px-1.5"
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </Button>
                    </NewEventPopover>
                  )}
                </div>

              </div>
            </div>

            {/* v1.6.0: adicionado `flex flex-col` para que o `flex-1` interno
                do SchedulingListView calcule altura corretamente. Sem isso,
                surgia uma segunda barra de scroll externa (wrapper). */}
            <div className="flex-1 min-h-0 p-2 flex flex-col">
              {viewMode === 'calendar' ? (
                <FullCalendar
                  ref={calendarRef}
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                  initialView={currentView}
                  initialDate={initialDate}
                  locale="pt-br"
                  firstDay={0}
                  height="100%"
                  headerToolbar={false}
                  dayMaxEvents={dayMaxEventsCurrent}
                  // v1.7.8: `moreLinkContent` fica como placeholder neutro
                  // (arg.num só reflete o que o FullCalendar tem em memória).
                  // A palavra final é reaplicada pelo `useEffect` +
                  // MutationObserver acima, usando o total real vindo de
                  // `monthSummary.perDay[data-date]`.
                  moreLinkContent={(arg) => {
                    return { html: `+${arg.num} mais` };
                  }}


                  moreLinkClick={() => 'popover'}
                  events={fcEvents}
                  eventOrder="start,id"
                  eventOrderStrict
                  editable
                  // v2.0.0 (UX): clique/arraste no grid cria evento manual
                  // já pré-preenchido com o slot escolhido.
                  selectable={canCreate}
                  selectMirror
                  select={(info) => {
                    if (!canCreate) return;
                    // v2.3.0: criação customizada recebe o slot escolhido
                    if (onCreateRequest) {
                      onCreateRequest({ start: info.start, end: info.end, allDay: info.allDay });
                      info.view.calendar.unselect();
                      return;
                    }
                    setEditingEvent(null);
                    // v2.2.0: cliques na visão Mês / faixa "dia inteiro" chegam
                    // com allDay=true e sem hora. Normalizamos para um evento
                    // com horário padrão para que o modal mostre hora/minutos.
                    setManualPrefill(
                      resolveDefaultEventTimes(info.start, info.end, info.allDay),
                    );
                    setModalOpen(true);
                    info.view.calendar.unselect();
                  }}

                  eventContent={(arg) => <CalendarEventContent arg={arg} />}
                  eventClick={(info) => handleGridEventClick(info.event.id, info.el as HTMLElement)}
                  eventDrop={handleEventChange}
                  eventResize={handleEventChange}

                  datesSet={(info) => {
                    // v1.6.1: `calendarRange` é a fonte de verdade no modo
                    // Calendário. Fetch só dispara depois deste callback rodar.
                    setCalendarRange({
                      start: info.start.toISOString(),
                      end: info.end.toISOString(),
                    });
                    setViewTitle(info.view.title);
                    setCurrentView(info.view.type as CalendarView);
                  }}
                  nowIndicator
                />
              ) : (
                /* Visualização em Lista — reuso 100% do SchedulingListView.
                   v1.6.0: scroll infinito via cursor (paridade com tela Agenda).
                   Sem wrapper com overflow — o próprio SchedulingListView é o
                   único container rolável, permitindo ao IntersectionObserver
                   interno disparar `fetchNextPage`. */
                <SchedulingListView
                  activities={listEvents.map(unifiedToActivityItem)}
                  isLoading={loadingListEvents}
                  hasNextPage={listHasNextPage}
                  fetchNextPage={listFetchNextPage}
                  isFetchingNextPage={listIsFetchingNextPage}
                  onOpenActivity={(item) => handleEventClick(item.id)}
                />
              )}
            </div>
          </div>
        </div>


        {/* Modal de evento manual */}
        <ManualEventModal
          open={modalOpen}
          onOpenChange={(open) => {
            setModalOpen(open);
            if (!open) {
              setEditingEvent(null);
              setManualPrefill(null);
            }
          }}
          calendars={calendars}
          event={editingEvent}
          defaultStartAt={manualPrefill?.startAt ?? null}
          defaultEndAt={manualPrefill?.endAt ?? null}
          defaultAllDay={manualPrefill?.allDay ?? false}
          defaultGoogleSync={manualPrefill?.googleSync ?? false}
        />


        {/* v2.1.0 — Popover de resumo ancorado no evento clicado no grid */}
        <CalendarEventQuickPopover
          event={quickView?.event ?? null}
          anchorRect={quickView?.rect ?? null}
          onClose={() => setQuickView(null)}
          onOpenFull={(ev) => {
            setQuickView(null);
            setSelectedEvent(ev);
          }}
          onDelete={async (ev) => {
            setQuickView(null);
            try {
              await deleteEventAsync(ev.id);
            } catch (err) {
              logger.error('Erro ao excluir evento pelo popover', err as Error);
            }
          }}
        />

        {/* Roteador de modais de detalhes por fonte do evento */}
        <CalendarEventModalRouter
          event={selectedEvent}
          calendars={calendars}
          onClose={() => setSelectedEvent(null)}
        />


        {/* Modal de configurações de calendários */}
        <CalendarSettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} />

        {/* Modal de criação de tarefa (fonte: task) */}
        <TaskFormDialog
          open={taskFormOpen}
          onOpenChange={setTaskFormOpen}
        />

        {/* Modal de criação de agendamento interno (fonte: booking) */}
        <InternalBookingDialog
          open={bookingOpen}
          onOpenChange={setBookingOpen}
        />
    </div>
  );
}
